import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggingService } from '@app/core/services';
import { DocumentStructureDTO, LlmResultResponse } from '@modules/registered-document-manager/models';
import { environment } from 'environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';

export interface DocumentRegisterResultResponse {
    success: boolean;
    message: string;
    data?: DocumentStructureDTO;
}

export interface OcrTextResponse {
    success: boolean;
    message: string;
    data?: string;
}

export interface DocumentColumnMappingResponse {
    supplierName?: string;
    data?: Array<{
        Source_Column?: string;
        source_Column?: string;
        CanonicalName?: string;
        canonicalName?: string;
        TargetCanonicalId?: string | number;
        NewColumnName?: string | null;
        newColumnName?: string | null;
    }>;
}

@Injectable({
    providedIn: 'root',
})
export class GetDocumentSettingsService {
    // Set the base URL of your API (adjust as needed)
    private apiUrl = environment.apiRoot;

    constructor(
        private http: HttpClient,
        private loggingService: LoggingService
    ) {}

    getDocumentRegisterResultStructure(
        docId: string | number,
        clientId: string,
        user: string
    ): Observable<DocumentRegisterResultResponse> {
        const url = `${this.apiUrl}documents/${docId}/structure`;
        const headers = new HttpHeaders({
            'X-ClientId': clientId,
            'X-User': user,
        });
        return this.http.get<DocumentRegisterResultResponse>(url, { headers }).pipe(
            tap((raw) => this.loggingService.debug('Service - raw API response:', raw)),
            map((raw) => {
                // If API signalled failure or no data, pass through
                if (!raw.success || !raw.data) {
                    return {
                        success: raw.success,
                        message: raw.message,
                    };
                }

                const r = raw.data;
                // 2) Translate PascalCase → camelCase here
                const dto: DocumentStructureDTO = {
                    docId: r.docId,
                    vendorName: r.vendorName,
                    details: r.details,
                    lineItems: r.lineItems,
                    documentImage: r.documentImage,
                    rawDocumentImage: r.rawDocumentImage,
                    value: r.value,
                };

                return {
                    success: true,
                    message: raw.message,
                    data: dto,
                };
            }),
            tap((mapped) => this.loggingService.debug('Service - mapped DTO response:', mapped))
        );
    }

    getDocumentRegisterResults$(): Observable<{}> {
        return of({});
    }

    getLlmResult(
        documentId: string,
        clientId: string,
        user: string
    ): Observable<LlmResultResponse> {
        const url = `${this.apiUrl}documents/${documentId}/llm-result`;
        const headers = new HttpHeaders({
            'X-ClientId': clientId,
            'X-User': user,
        });

        return this.http.get<LlmResultResponse>(url, { headers }).pipe(
            tap((raw) => this.loggingService.debug('Service - LLM raw API response:', raw)),
            map((raw) => {
                if (!raw.success || !raw.data) {
                    return {
                        success: raw.success,
                        message: raw.message,
                    };
                }

                return {
                    success: true,
                    message: raw.message,
                    data: raw.data,
                };
            }),
            tap((mapped) => this.loggingService.debug('Service - LLM mapped response:', mapped))
        );
    }

    getOcrNormalizedText(
        documentId: string,
        clientId: string,
        user: string
    ): Observable<OcrTextResponse> {
        const url = `${this.apiUrl}documents/${documentId}/ocr-text`;
        const headers = new HttpHeaders({
            'X-ClientId': clientId,
            'X-User': user,
        });

        return this.http.get<OcrTextResponse>(url, { headers }).pipe(
            tap((raw) => this.loggingService.debug('Service - OCR text raw API response:', raw)),
            map((raw) => {
                if (!raw.success || !raw.data) {
                    return {
                        success: raw.success,
                        message: raw.message,
                    };
                }

                return {
                    success: true,
                    message: raw.message,
                    data: raw.data,
                };
            }),
            tap((mapped) => this.loggingService.debug('Service - OCR text mapped response:', mapped))
        );
    }

    getLlmResultRawJson(
        documentId: string,
        clientId: string,
        user: string
    ): Observable<any> {
        const url = `${this.apiUrl}documents/${documentId}/llm-result-raw`;
        const headers = new HttpHeaders({
            'X-ClientId': clientId,
            'X-User': user,
        });

        return this.http.get(url, { headers, responseType: 'text' }).pipe(
            tap((raw) => this.loggingService.debug('Service - Extracted result response received')),
            map((jsonString) => {
                try {
                    return JSON.parse(jsonString);
                } catch (error) {
                    this.loggingService.error('Failed to parse extracted result JSON:', error);
                    return null;
                }
            }),
            catchError((error) => {
                this.loggingService.error('HTTP Error fetching extracted result:', error);
                return throwError(() => error);
            })
        );
    }

    getDocumentColumnMapping(
        documentId: string | number,
        clientId: string,
        user: string,
        supplierName?: string
    ): Observable<DocumentColumnMappingResponse | null> {
        const url = `${this.apiUrl}documents/column-mapping`;
        const headers = new HttpHeaders({
            'X-ClientId': clientId,
            'X-User': user,
        });
        const params: { documentId: string; supplierName?: string } = {
            documentId: String(documentId),
        };
        if (supplierName) {
            params.supplierName = supplierName;
        }

        return this.http.get(url, { headers, params, responseType: 'text' }).pipe(
            tap(() => this.loggingService.debug('Service - Column mapping response received')),
            map((jsonString) => {
                try {
                    return JSON.parse(jsonString) as DocumentColumnMappingResponse;
                } catch (error) {
                    this.loggingService.error('Failed to parse column mapping JSON:', error);
                    return null;
                }
            }),
            catchError((error) => {
                this.loggingService.error('HTTP Error fetching column mapping:', error);
                return throwError(() => error);
            })
        );
    }
}
